/**
 * Performance Provider Component
 *
 * Wraps the application to provide performance monitoring capabilities.
 * Initializes the performance monitor, tracks route changes, and integrates API tracking.
 */

import React, { useEffect, useRef, createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  performanceMonitor,
  createAPIPerformanceInterceptor,
  PerformanceBudget,
  WebVitalsMetric,
  BudgetViolation,
  DEFAULT_PERFORMANCE_BUDGETS,
} from '@/services/performanceMonitoring';
import { PerformanceBudgetWarning } from '@/components/PerformanceBudgetWarning';
import api from '@/services/api';
import { logger } from '@/utils/logger';

// =============================================================================
// CONTEXT
// =============================================================================

export interface PerformanceContextValue {
  /**
   * Whether performance monitoring is enabled
   */
  isEnabled: boolean;

  /**
   * Get current web vitals
   */
  getWebVitals: () => WebVitalsMetric[];

  /**
   * Get budget violations
   */
  getBudgetViolations: () => BudgetViolation[];

  /**
   * Get current budgets
   */
  getBudgets: () => PerformanceBudget;

  /**
   * Update performance budgets
   */
  setBudgets: (budgets: Partial<PerformanceBudget>) => void;

  /**
   * Manually send performance report
   */
  sendReport: () => Promise<void>;

  /**
   * Track a custom metric
   */
  trackMetric: (name: string, value: number, context?: string) => void;
}

const PerformanceContext = createContext<PerformanceContextValue | null>(null);

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to access performance monitoring context
 */
export function usePerformanceContext(): PerformanceContextValue {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error(
      'usePerformanceContext must be used within a PerformanceProvider'
    );
  }
  return context;
}

// =============================================================================
// PROPS
// =============================================================================

export interface PerformanceProviderProps {
  children: React.ReactNode;

  /**
   * Whether to enable performance monitoring
   * @default true
   */
  enabled?: boolean;

  /**
   * User ID for tracking (optional)
   */
  userId?: string;

  /**
   * Custom performance budgets
   */
  budgets?: Partial<PerformanceBudget>;

  /**
   * Custom reporting endpoint
   */
  reportingEndpoint?: string;

  /**
   * Reporting interval in milliseconds
   * @default 30000
   */
  reportingInterval?: number;

  /**
   * Whether to show budget warnings UI
   * @default true in development
   */
  showBudgetWarnings?: boolean;

  /**
   * Callback when a budget violation occurs
   */
  onBudgetViolation?: (violation: BudgetViolation) => void;

  /**
   * Whether to track API calls automatically
   * @default true
   */
  trackAPIAutomatically?: boolean;
}

// =============================================================================
// ROUTE TRACKER COMPONENT
// =============================================================================

const RouteTracker: React.FC = () => {
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);
  const navigationIdRef = useRef<string | null>(null);

  useEffect(() => {
    const currentPath = location.pathname + location.search;

    // First render - just record the initial path
    if (previousPathRef.current === null) {
      previousPathRef.current = currentPath;
      return;
    }

    // Same path - no tracking needed
    if (previousPathRef.current === currentPath) {
      return;
    }

    // Start tracking route change
    navigationIdRef.current = performanceMonitor.startRouteChange(
      previousPathRef.current,
      currentPath
    );

    // End tracking after render completes
    const completeTracking = () => {
      if (navigationIdRef.current) {
        performanceMonitor.endRouteChange(navigationIdRef.current, currentPath);
        navigationIdRef.current = null;
      }
      previousPathRef.current = currentPath;
    };

    // Use requestIdleCallback for best accuracy
    if ('requestIdleCallback' in window) {
      (window as Window & { requestIdleCallback: (callback: () => void, options?: { timeout: number }) => number }).requestIdleCallback(completeTracking, { timeout: 100 });
    } else {
      requestAnimationFrame(() => {
        setTimeout(completeTracking, 0);
      });
    }
  }, [location]);

  return null;
};

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================

export const PerformanceProvider: React.FC<PerformanceProviderProps> = ({
  children,
  enabled = true,
  userId,
  budgets,
  reportingEndpoint,
  reportingInterval = 30000,
  showBudgetWarnings,
  onBudgetViolation,
  trackAPIAutomatically = true,
}) => {
  const isInitializedRef = useRef(false);

  // Determine if we should show budget warnings
  const shouldShowWarnings = useMemo(() => {
    if (showBudgetWarnings !== undefined) {
      return showBudgetWarnings;
    }
    return process.env.NODE_ENV === 'development';
  }, [showBudgetWarnings]);

  const shouldLogWarnings = shouldShowWarnings;

  // Initialize performance monitoring
  useEffect(() => {
    if (!enabled || isInitializedRef.current) return;

    performanceMonitor.init({
      userId,
      budgets,
      reportingEndpoint,
      reportingInterval,
      onBudgetViolation: (violation) => {
        if (shouldLogWarnings) {
          logger.warn('[Performance] Budget violation', violation);
        }
        if (onBudgetViolation) {
          onBudgetViolation(violation);
        }
      },
    });

    isInitializedRef.current = true;
    logger.info('[Performance] Provider initialized');

    return () => {
      performanceMonitor.destroy();
      isInitializedRef.current = false;
    };
  }, [
    enabled,
    userId,
    budgets,
    reportingEndpoint,
    reportingInterval,
    onBudgetViolation,
    shouldLogWarnings,
  ]);

  // Update user ID when it changes
  useEffect(() => {
    if (userId) {
      performanceMonitor.setUserId(userId);
    }
  }, [userId]);

  // Set up API tracking
  useEffect(() => {
    if (!enabled || !trackAPIAutomatically) return;

    createAPIPerformanceInterceptor(api);
    logger.debug('[Performance] API tracking enabled');
  }, [enabled, trackAPIAutomatically]);

  // Context value
  const contextValue = useMemo<PerformanceContextValue>(
    () => ({
      isEnabled: enabled,
      getWebVitals: () => performanceMonitor.getWebVitals(),
      getBudgetViolations: () => performanceMonitor.getBudgetViolations(),
      getBudgets: () => performanceMonitor.getBudgets(),
      setBudgets: (newBudgets) => performanceMonitor.setBudgets(newBudgets),
      sendReport: () => performanceMonitor.sendReport(),
      trackMetric: (name, value, context) => {
        // Use component render tracking for custom metrics
        performanceMonitor.trackComponentRender({
          componentName: name,
          renderTime: value,
          updateCount: 1,
          props: context ? { context } : undefined,
        });
      },
    }),
    [enabled]
  );

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <PerformanceContext.Provider value={contextValue}>
      <RouteTracker />
      {children}
      {shouldShowWarnings && (
        <PerformanceBudgetWarning
          budgets={budgets}
          onViolation={onBudgetViolation}
          devOnly={!showBudgetWarnings}
        />
      )}
    </PerformanceContext.Provider>
  );
};

export default PerformanceProvider;
